import type {
  CompetitorTeaserDto,
  CompetitorTeaserHighlightDto,
} from '@up2stay/up2promo-api-client';
import { Button, Card, cn } from '@up2stay/up2promo-ui';
import { ArrowRight, Radar } from 'lucide-react';

import { REPUTATION_SECTION_TITLE_CLASS } from '@/core/components/reputation/reputation-surface';

/** Gaps smaller than this count as tied (matches the backend teaser). */
const RATING_GAP_EPSILON = 0.05;

export type OverviewCompetitorCardLabels = {
  title: string;
  subtitleTiedFewer: string;
  subtitleTiedMore: string;
  subtitleAhead: string;
  subtitleBehind: string;
  subtitleNoReviews: string;
  trackedCount: string;
  viewComparison: string;
  company: string;
  rating: string;
  count: string;
  volume: string;
  you: string;
  emptyDescription: string;
  emptyCta: string;
};

type Row = {
  key: string;
  name: string;
  rating?: number;
  count?: number;
  isYou: boolean;
};

function buildRows(teaser: CompetitorTeaserDto): Row[] {
  const highlights: CompetitorTeaserHighlightDto[] = teaser.highlights ?? [];
  const rows: Row[] = [];

  const you = teaser.aggregate
    ? {
        rating: teaser.aggregate.yourAverageRating,
        count: teaser.aggregate.yourReviewCount,
      }
    : highlights[0]
      ? {
          rating: highlights[0].yourRating,
          count: highlights[0].yourReviewCount,
        }
      : null;
  if (you) {
    // No own reviews: no rating to show (an average of 0.0 would be a lie).
    rows.push({
      key: 'you',
      name: '',
      isYou: true,
      rating: (you.count ?? 0) > 0 ? you.rating : undefined,
      count: you.count,
    });
  }
  for (const highlight of highlights) {
    rows.push({
      key: `${highlight.locationId}:${highlight.competitorName}`,
      name: highlight.competitorName,
      rating: highlight.competitorRating,
      count: highlight.competitorReviewCount,
      isYou: false,
    });
  }
  return rows;
}

type Standing = 'tiedFewer' | 'tiedMore' | 'ahead' | 'behind' | 'noReviews';

function standing(rows: Row[]): Standing {
  const you = rows.find((row) => row.isYou);
  const rivals = rows.filter((row) => !row.isYou && row.rating != null);
  if (you?.rating == null) return 'noReviews';
  const yourRating = you.rating;
  const behind = rivals.some(
    (row) => (row.rating ?? 0) - yourRating > RATING_GAP_EPSILON,
  );
  const ahead = rivals.every(
    (row) => yourRating - (row.rating ?? 0) > RATING_GAP_EPSILON,
  );
  if (behind) return 'behind';
  if (ahead && rivals.length > 0) return 'ahead';
  const topRivalCount = Math.max(...rivals.map((row) => row.count ?? 0), 0);
  return (you?.count ?? 0) < topRivalCount ? 'tiedFewer' : 'tiedMore';
}

export function OverviewCompetitorCard({
  teaser,
  labels,
  locale,
  onOpen,
}: {
  teaser: CompetitorTeaserDto;
  labels: OverviewCompetitorCardLabels;
  locale: string;
  onOpen: () => void;
}) {
  const rows = buildRows(teaser);
  const hasComparison =
    teaser.status !== 'none' && rows.some((row) => !row.isYou);

  if (!hasComparison) {
    return (
      <Card className="flex min-w-0 flex-col items-start gap-4 p-5 sm:p-6">
        <div className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-[var(--r-control)] bg-muted text-muted-foreground">
            <Radar className="size-4" aria-hidden />
          </span>
          <h2 className={REPUTATION_SECTION_TITLE_CLASS}>{labels.title}</h2>
        </div>
        <p className="text-muted-foreground text-[13.5px] leading-relaxed">
          {labels.emptyDescription}
        </p>
        <Button variant="ghostAccent" size="sm" onClick={onOpen}>
          {labels.emptyCta}
          <ArrowRight className="ml-1 size-3.5" aria-hidden />
        </Button>
      </Card>
    );
  }

  const subtitles: Record<Standing, string> = {
    tiedFewer: labels.subtitleTiedFewer,
    tiedMore: labels.subtitleTiedMore,
    ahead: labels.subtitleAhead,
    behind: labels.subtitleBehind,
    noReviews: labels.subtitleNoReviews,
  };
  const maxCount = Math.max(...rows.map((row) => row.count ?? 0), 1);
  const trackedCount = teaser.totalCompetitorCount ?? rows.length - 1;

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className={REPUTATION_SECTION_TITLE_CLASS}>{labels.title}</h2>
          <p className="text-muted-foreground mt-0.5 text-[12.5px]">
            {subtitles[standing(rows)]}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">
            {labels.trackedCount.replace('{count}', String(trackedCount))}
          </span>
          <Button variant="ghostAccent" size="sm" onClick={onOpen}>
            {labels.viewComparison}
            <ArrowRight className="ml-1 size-3.5" aria-hidden />
          </Button>
        </div>
      </div>

      <div
        role="table"
        aria-label={labels.title}
        className="min-w-0 text-[13px]"
      >
        <div
          role="row"
          className="text-muted-foreground grid grid-cols-[minmax(0,1.4fr)_.7fr_.7fr] gap-3 border-b py-2 max-md:hidden text-[11.5px] sm:grid-cols-[minmax(0,1.4fr)_.8fr_.8fr_1.6fr]"
        >
          <span role="columnheader">{labels.company}</span>
          <span role="columnheader">{labels.rating}</span>
          <span role="columnheader">{labels.count}</span>
          <span role="columnheader" className="hidden sm:block">
            {labels.volume}
          </span>
        </div>
        {rows.map((row) => (
          <div
            key={row.key}
            role="row"
            className="grid grid-cols-[minmax(0,1.4fr)_.7fr_.7fr] items-center gap-3 border-b py-3 last:border-b-0 sm:grid-cols-[minmax(0,1.4fr)_.8fr_.8fr_1.6fr]"
          >
            <span
              role="cell"
              className={cn(
                'flex min-w-0 items-center gap-2',
                row.isYou && 'font-semibold',
              )}
            >
              <span className="truncate">
                {row.isYou ? labels.you : row.name}
              </span>
            </span>
            <span role="cell" className="tabular-nums">
              {row.rating != null ? `★ ${row.rating.toFixed(1)}` : '—'}
            </span>
            <span role="cell" className="font-mono tabular-nums">
              {row.count != null ? row.count.toLocaleString(locale) : '—'}
            </span>
            <span role="cell" className="hidden sm:block">
              <span className="block h-1.5 rounded-full bg-muted">
                <span
                  className={cn(
                    'block h-full rounded-full',
                    row.isYou ? 'bg-primary' : 'bg-[color:var(--chart-3)]',
                  )}
                  style={{
                    width: `${Math.max(3, ((row.count ?? 0) / maxCount) * 100)}%`,
                  }}
                />
              </span>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}
